/**
 * FollowPage — Discover users to follow + manage Friend Groups
 * Route: /following
 */
import { useState, useEffect } from 'react';
import { ArrowLeft, Users, UserPlus, UserCheck, Plus, X, Search, FolderPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FollowPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('discover'); // discover | following | groups
  const [people, setPeople] = useState([]);
  const [following, setFollowing] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [defaultGroups, setDefaultGroups] = useState([]);

  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/social/suggested-people`, { credentials: 'include', headers }).then(r => r.json()).catch(() => ({ people: [] })),
      fetch(`${API_URL}/api/social/following`, { credentials: 'include', headers }).then(r => r.json()).catch(() => ({ following: [] })),
      fetch(`${API_URL}/api/groups`, { credentials: 'include', headers }).then(r => r.json()).catch(() => ({ groups: [], default_names: [] })),
    ]).then(([p, f, g]) => {
      setPeople(p.people || []);
      setFollowing(f.following || []);
      setGroups(g.groups || []);
      setDefaultGroups(g.default_names || []);
    }).finally(() => setLoading(false));
  }, [user]);

  const handleFollow = async (userId) => {
    await fetch(`${API_URL}/api/social/follow/${userId}`, { method: 'POST', credentials: 'include', headers });
    setFollowing(prev => [...prev, { user_id: userId }]);
    setPeople(prev => prev.map(p => p.user_id === userId ? { ...p, is_following: true } : p));
  };

  const handleUnfollow = async (userId) => {
    await fetch(`${API_URL}/api/social/unfollow/${userId}`, { method: 'POST', credentials: 'include', headers });
    setFollowing(prev => prev.filter(f => f.user_id !== userId && f.following_id !== userId));
    setPeople(prev => prev.map(p => p.user_id === userId ? { ...p, is_following: false } : p));
  };

  const handleCreateGroup = async (name) => {
    const res = await fetch(`${API_URL}/api/groups`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'include',
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      const g = await res.json();
      setGroups(prev => [...prev, g]);
    }
    setShowCreateGroup(false);
    setNewGroupName('');
  };

  const handleAddToGroup = async (groupId, userId) => {
    await fetch(`${API_URL}/api/groups/${groupId}/add`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'include',
      body: JSON.stringify({ user_id: userId }),
    });
    // Refresh groups
    const res = await fetch(`${API_URL}/api/groups`, { credentials: 'include', headers });
    if (res.ok) { const d = await res.json(); setGroups(d.groups || []); }
  };

  const handleDeleteGroup = async (groupId) => {
    await fetch(`${API_URL}/api/groups/${groupId}`, { method: 'DELETE', credentials: 'include', headers });
    setGroups(prev => prev.filter(g => g.group_id !== groupId));
  };

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center"><p className="text-gray-500 mb-3">Sign in to follow people</p><Link to="/profile" className="text-[#FF6B35] font-semibold">Sign In</Link></div>
    </div>
  );

  const TABS = [
    { key: 'discover', label: 'Discover People' },
    { key: 'following', label: `Following (${following.length})` },
    { key: 'groups', label: `Groups (${groups.length})` },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      {/* Hero */}
      <div className="relative" style={{ minHeight: '160px' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF8C42] to-[#F4C430]" />
        <div className="relative flex flex-col justify-end max-w-3xl mx-auto px-4" style={{ minHeight: '160px' }}>
          <div className="mb-5">
            <Link to="/profile" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs mb-2"><ArrowLeft className="w-3 h-3" /> Profile</Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users className="w-7 h-7" /> People & Groups</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 flex gap-1 py-2">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${tab === t.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5">
        {/* Search */}
        {tab !== 'groups' && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search people..."
              className="w-full pl-10 pr-4 py-2.5 bg-white rounded-2xl text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/30" />
          </div>
        )}

        {/* Discover */}
        {tab === 'discover' && (
          <div className="space-y-2">
            {loading ? [1,2,3,4].map(i => <div key={i} className="h-16 bg-gray-100 rounded-2xl animate-pulse" />) :
            people.filter(p => !searchQ || p.name?.toLowerCase().includes(searchQ.toLowerCase())).map(p => (
              <div key={p.user_id} className="flex items-center gap-3 bg-white rounded-2xl p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <Link to={`/profile/${p.user_id}`}>
                  <Avatar className="w-11 h-11">
                    <AvatarImage src={p.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white font-bold">{(p.name || 'F')[0]}</AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                  <p className="text-xs text-gray-500 truncate">{p.username || p.bio || ''}</p>
                </div>
                <button onClick={() => p.is_following ? handleUnfollow(p.user_id) : handleFollow(p.user_id)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${p.is_following ? 'bg-[#FFF5F0] text-[#FF6B35] border border-[#FF6B35]/20' : 'bg-gray-900 text-white'}`}>
                  {p.is_following ? <><UserCheck className="w-3 h-3 inline mr-1" />Following</> : <><UserPlus className="w-3 h-3 inline mr-1" />Follow</>}
                </button>
              </div>
            ))}
            {!loading && people.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No suggestions yet — start posting to discover foodies!</p>}
          </div>
        )}

        {/* Following */}
        {tab === 'following' && (
          <div className="space-y-2">
            {following.filter(f => !searchQ || f.name?.toLowerCase().includes(searchQ.toLowerCase())).map(f => (
              <div key={f.user_id || f.following_id} className="flex items-center gap-3 bg-white rounded-2xl p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <Avatar className="w-11 h-11">
                  <AvatarImage src={f.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white font-bold">{(f.name || 'F')[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{f.name || 'Foodie'}</p>
                </div>
                {/* Add to group dropdown */}
                {groups.length > 0 && (
                  <select onChange={e => { if (e.target.value) handleAddToGroup(e.target.value, f.user_id || f.following_id); e.target.value = ''; }}
                    className="text-[10px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-gray-500">
                    <option value="">+ Group</option>
                    {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                  </select>
                )}
                <button onClick={() => handleUnfollow(f.user_id || f.following_id)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-full bg-[#FFF5F0] text-[#FF6B35] border border-[#FF6B35]/20">
                  Unfollow
                </button>
              </div>
            ))}
            {following.length === 0 && <p className="text-center text-gray-400 text-sm py-8">Not following anyone yet</p>}
          </div>
        )}

        {/* Groups */}
        {tab === 'groups' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">{groups.length} groups</span>
              <button onClick={() => setShowCreateGroup(!showCreateGroup)}
                className="h-9 px-4 bg-gray-900 text-white rounded-2xl text-sm font-semibold flex items-center gap-1.5">
                <FolderPlus className="w-4 h-4" />New Group
              </button>
            </div>

            {showCreateGroup && (
              <div className="bg-white rounded-[20px] p-4 mb-4 shadow-sm">
                <div className="flex gap-2 mb-3 flex-wrap">
                  {defaultGroups.map(n => (
                    <button key={n} onClick={() => handleCreateGroup(n)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full hover:bg-gray-200">{n}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Custom group name..."
                    className="flex-1 px-3 py-2 bg-gray-50 rounded-xl text-sm focus:outline-none" />
                  <button onClick={() => newGroupName.trim() && handleCreateGroup(newGroupName)}
                    className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl">Create</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {groups.map(g => (
                <div key={g.group_id} className="bg-white rounded-[20px] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{g.icon || '👥'}</span>
                      <h3 className="text-sm font-bold text-gray-900">{g.name}</h3>
                      <span className="text-xs text-gray-400">{g.member_count} members</span>
                    </div>
                    <button onClick={() => handleDeleteGroup(g.group_id)} className="p-1 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                  {g.member_details?.length > 0 ? (
                    <div className="flex -space-x-2">
                      {g.member_details.slice(0, 8).map(m => (
                        <Avatar key={m.user_id} className="w-8 h-8 ring-2 ring-white">
                          <AvatarImage src={m.picture} />
                          <AvatarFallback className="bg-gray-200 text-[8px] font-bold">{(m.name || 'F')[0]}</AvatarFallback>
                        </Avatar>
                      ))}
                      {g.member_count > 8 && <div className="w-8 h-8 rounded-full bg-gray-100 ring-2 ring-white flex items-center justify-center text-[9px] text-gray-500 font-bold">+{g.member_count - 8}</div>}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No members yet — add from Following tab</p>
                  )}
                </div>
              ))}
              {groups.length === 0 && !showCreateGroup && <p className="text-center text-gray-400 text-sm py-8">Create groups to organize your food friends</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FollowPage;
